import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Table, Input, Avatar, Tag, Row, Col, Select } from "antd";
import { Users } from "lucide-react";
import { format } from "date-fns";
import { getPortalCommunityMembers } from "services/communities.service";
import { renderErrorNotifications } from "helpers/error.helpers";
import { MEMBERSHIP_STATUS, MEMBERSHIP_STATUS_COLOR } from "commons/constants";

const PAGE_SIZE = 10;

// Default "active" so the count matches the Active members tile and the
// community list card (people with current access).
const STATUS_OPTIONS = [
  { value: "active", label: "Active members", totalLabel: "active members" },
  { value: "expired", label: "Lapsed", totalLabel: "lapsed memberships" },
  {
    value: "cancelled",
    label: "Cancelled",
    totalLabel: "cancelled memberships",
  },
  { value: "pending", label: "Pending", totalLabel: "pending memberships" },
  { value: "failed", label: "Failed", totalLabel: "failed memberships" },
  {
    value: "",
    label: "All statuses",
    totalLabel: "memberships (all statuses)",
  },
];

const COLUMNS = [
  {
    title: "Member",
    key: "member",
    render: (_, r) => (
      <div className="flex items-center gap-3">
        <Avatar
          src={r.profile_image_url}
          size={36}
          icon={<Users size={16} />}
        />
        <div>
          <p className="font-medium text-sm leading-tight">{r.full_name}</p>
          <p className="text-xs text-muted-foreground">{r.email_id}</p>
        </div>
      </div>
    ),
  },
  {
    title: "Phone",
    dataIndex: "phone_number",
    key: "phone_number",
    render: (v) => <span className="text-sm">{v}</span>,
  },
  {
    title: "Plan",
    key: "plan",
    render: (_, r) => (
      <div>
        <p className="text-sm font-medium">{r.plan_name}</p>
        <p className="text-xs text-muted-foreground">{r.tier_name}</p>
      </div>
    ),
  },
  {
    title: "Billing",
    dataIndex: "billing_type",
    key: "billing_type",
    render: (v) => (
      <span className="text-xs capitalize">{v?.toLowerCase()}</span>
    ),
  },
  {
    title: "Status",
    dataIndex: "membership_status",
    key: "membership_status",
    render: (v) => (
      <Tag color={MEMBERSHIP_STATUS_COLOR[MEMBERSHIP_STATUS[v]] ?? "default"}>
        {MEMBERSHIP_STATUS[v] ?? v ?? "—"}
      </Tag>
    ),
  },
  {
    title: "End date",
    dataIndex: "membership_end_date",
    key: "membership_end_date",
    render: (v) => (
      <span className="text-sm text-muted-foreground">
        {v && format(new Date(v), "MMM d, yyyy")}
      </span>
    ),
  },
];

function MembersTab() {
  const { communityId } = useParams();
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  const loadMembers = useCallback(
    async (pageNum) => {
      if (!communityId) return;
      setLoading(true);
      const { data, errors } = await getPortalCommunityMembers(communityId, {
        current_page: pageNum,
        page_size: PAGE_SIZE,
        membership_status: statusFilter,
      });
      setLoading(false);
      if (errors?.length > 0) {
        renderErrorNotifications(errors);
        return;
      }
      setMembers(data?.data ?? []);
      setTotal(data?.meta?.pagination?.total ?? 0);
    },
    [communityId, statusFilter]
  );

  useEffect(() => {
    loadMembers(page);
  }, [loadMembers, page]);

  const totalLabel =
    STATUS_OPTIONS.find((o) => o.value === statusFilter)?.totalLabel ??
    "memberships";

  const q = search.toLowerCase();
  const filtered = !q
    ? members
    : members.filter((m) =>
        [m.full_name, m.email_id, m.phone_number, m.plan_name, m.tier_name]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );

  return (
    <div className="space-y-4 animate-in">
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={8} md={6}>
          <Input.Search
            placeholder="Search this page…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            allowClear
          />
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Select
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(1);
              setSearch("");
            }}
            options={STATUS_OPTIONS}
            style={{ width: "100%" }}
          />
        </Col>
      </Row>

      <Table
        rowKey="membership_id"
        columns={COLUMNS}
        dataSource={filtered}
        loading={loading}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          onChange: (p) => {
            setPage(p);
            setSearch("");
          },
          showSizeChanger: false,
          showTotal: (t) => `${t} ${totalLabel}`,
        }}
        scroll={{ x: 700 }}
        size="small"
        locale={{
          emptyText: (
            <div className="py-10 text-center text-muted-foreground">
              <Users className="h-10 w-10 mx-auto opacity-20 mb-3" />
              <p className="text-sm">No members found.</p>
            </div>
          ),
        }}
      />
    </div>
  );
}

export default MembersTab;
